import { fetchJsonWithAuth } from "./authApi";
import { AUTH_URL } from "./apiConfig";

const unwrap = (json) => json?.data ?? json;

/**
 * Converts a backend "KV array" candidate — [{Key, Value}, {Key, Value}, ...] —
 * into a normal flat object { Key: Value, ... }.
 */
function flattenKvItem(item) {
  if (Array.isArray(item) && item.every(p => p && typeof p === 'object' && 'Key' in p)) {
    return item.reduce((acc, { Key, Value }) => {
      acc[Key] = Value;
      return acc;
    }, {});
  }
  return item;
}

export function extractStageCandidates(stage) {
  if (!stage) return [];
  if (Array.isArray(stage.data)) return stage.data.map(flattenKvItem);
  if (stage.stage && Array.isArray(stage.stage.data)) return stage.stage.data.map(flattenKvItem);
  if (Array.isArray(stage)) return stage.map(flattenKvItem);
  return [];
}

export async function getWorkflowByEntity(entityId) {
  try {
    const json = await fetchJsonWithAuth(
      AUTH_URL,
      `/workflow/entity/${entityId}?workflowType=HiringPipeline`
    );
    const data = unwrap(json);
    return data ?? null;
  } catch (error) {
    if (error?.status === 404 || error?.message?.includes('404')) {
      return null;
    }
    throw error;
  }
}

export async function getWorkflowById(workflowId) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/workflow/${workflowId}`);
  return unwrap(json);
}

export async function getWorkflowStage(workflowId, stageName) {
  const json = await fetchJsonWithAuth(
    AUTH_URL,
    `/workflow/${workflowId}/stage/${stageName}`
  );
  let result = unwrap(json);
  if (Array.isArray(result)) {
    return { data: result };
  }
  return result;
}

/**
 * Get a single candidate's full profile by reference ID.
 * Reuses the existing /module-data-list gateway (same one used to list Jobs)
 * filtered down to module=candidate and a single id.
 */
export async function getCandidateByReferenceId(referenceId) {
  if (!referenceId) return null;

  try {
    const json = await fetchJsonWithAuth(
      AUTH_URL,
      `/module-data-list?module=candidate&id=${referenceId}&limit=1&offset=0`
    );
    const data = unwrap(json);

    if (Array.isArray(data)) return data[0] ?? null;
    if (data?.data && Array.isArray(data.data)) return data.data[0] ?? null;
    const items = data?.rows ?? data?.records ?? data?.items ?? data?.list;
    if (Array.isArray(items)) return items[0] ?? null;
    return data ?? null;
  } catch (err) {
    console.error("[getCandidateByReferenceId] failed:", referenceId, err);
    return null;
  }
}

/**
 * Get a single submission's full record by submission ID.
 * Submission-level fields (pay rate, job, internal/client status, submitted by)
 * live on the submissions collection — NOT on the candidate record — so this
 * is a separate call from getCandidateByReferenceId.
 */
export async function getSubmissionById(submissionId) {
  if (!submissionId) return null;

  try {
    const json = await fetchJsonWithAuth(
      AUTH_URL,
      `/module-data-list?module=submissions&id=${submissionId}&limit=1&offset=0`
    );
    const data = unwrap(json);

    if (Array.isArray(data)) return data[0] ?? null;
    if (data?.data && Array.isArray(data.data)) return data.data[0] ?? null;
    const items = data?.rows ?? data?.records ?? data?.items ?? data?.list;
    if (Array.isArray(items)) return items[0] ?? null;
    return data ?? null;
  } catch (err) {
    console.error("[getSubmissionById] failed:", submissionId, err);
    return null;
  }
}

export async function createWorkflow(payload) {
  const json = await fetchJsonWithAuth(AUTH_URL, "/workflow/create", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return unwrap(json);
}

export async function updateWorkflowStage(workflowId, payload) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/workflow/${workflowId}/stage`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return unwrap(json);
}

export async function getWorkflowDashboard(module) {
  const json = await fetchJsonWithAuth(
    AUTH_URL,
    `/workflow/dashboard?module=${module}&workflowType=HiringPipeline`
  );
  return unwrap(json);
}

export async function deleteWorkflow(workflowId) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/workflow/${workflowId}`, {
    method: "DELETE",
  });
  return unwrap(json);
}
